import React, { memo } from 'react';

import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup as MuiRadioGroup,
  Typography,
} from '@mui/material';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { ItemGroupAlignment, ItemLabelPlacement } from 'constants/ui';

import FieldWrapper from '../FieldWrapper';
import HelperText from '../HelperText';
import Label from '../Label';

import { formControlLabelStyles, getFormControlStyles } from './RadioGroup.styles';

const RadioGroup = ({
  groupName,
  label = '',
  onChange,
  options,
  value = '',
  dataTestId = '',
  disabled = false,
  labelPlacement = ItemLabelPlacement.START,
  helperText = '',
  align = ItemGroupAlignment.HORIZONTAL,
  radioGroupAlign = ItemGroupAlignment.HORIZONTAL,
  required = false,
  error = false,
  width = '100%',
  columns,
  customStyles,
  inputProps,
  containerClassName = '',
  customOnChange,
  iconProps,
  isFieldChanged = false,
  ...props
}) => {
  const formattedValue = value?.toString?.();
  const isOptionsExist = Array.isArray(options);

  return (
    <FormControl
      className={clsx('form-control', containerClassName)}
      columns={columns}
      component={FieldWrapper}
      disabled={disabled}
      sx={getFormControlStyles({ align, customStyles, error, columns, isFieldChanged })}
      variant="outlined"
      width={width}
    >
      {label && (
        <Label error={error} name={groupName} required={required}>
          {label}
        </Label>
      )}

      {isOptionsExist && (
        <MuiRadioGroup
          {...props}
          aria-label={groupName}
          className="radio-group"
          data-is-error={error}
          data-testid={dataTestId}
          id={groupName}
          name={groupName}
          row={radioGroupAlign === ItemGroupAlignment.HORIZONTAL}
          value={formattedValue}
          onChange={customOnChange || onChange}
        >
          {options?.map(({ label: optionLabel, name, value: optionValue }) => {
            const label = optionLabel || name || '';
            const isChecked = optionValue === formattedValue;

            return (
              <FormControlLabel
                className="form-control-label"
                control={
                  <Radio
                    disableTouchRipple
                    checked={isChecked}
                    className="radio-button-icon"
                    inputProps={inputProps}
                    {...iconProps}
                  />
                }
                data-testid={optionValue}
                disabled={disabled}
                key={label}
                label={
                  <Typography
                    className={clsx('radio-button-label', { 'Mui-disabled': disabled })}
                    variant="regular14"
                  >
                    {label}
                  </Typography>
                }
                labelPlacement={labelPlacement}
                sx={formControlLabelStyles}
                value={optionValue}
              />
            );
          })}
        </MuiRadioGroup>
      )}
      <HelperText error={error}>{helperText}</HelperText>
    </FormControl>
  );
};

RadioGroup.propTypes = {
  align: PropTypes.string,
  columns: PropTypes.number,
  customOnChange: PropTypes.func,
  customStyles: PropTypes.object,
  dataTestId: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  groupName: PropTypes.string,
  helperText: PropTypes.string,
  iconProps: PropTypes.object,
  inputProps: PropTypes.object,
  label: PropTypes.node,
  labelPlacement: PropTypes.string,
  options: PropTypes.array,
  radioGroupAlign: PropTypes.string,
  required: PropTypes.bool,
  isFieldChanged: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  width: PropTypes.string,
  containerClassName: PropTypes.string,
  onChange: PropTypes.func,
};

export default memo(RadioGroup);
